import { UseFormWatch } from "react-hook-form";

export const useGetTotalBreakDownValues = (watch: UseFormWatch<any>) => {
  const values = watch();

  const items = (values?.items ?? []) as Array<any>;
  const taxesArray = (values?.taxes ?? []) as Array<any>;
  const discountType: string = values?.discountType ?? "amount";
  const formLevelDiscountRaw = Number(values?.totalDiscount) || 0;

  // ── Item-level subtotal (post item-discount base, pre-tax) ──────────────
  const subtotal = items.reduce((sum: number, item: any) => {
    const gross = (item?.unitPrice && item?.unitPrice * item?.quantity) || 0;
    const dto = item?.dto ?? 0;
    const itemDtoType: string = item?.dtoType ?? "amount";
    const discountAmount =
      dto > 0 ? (itemDtoType === "percentage" ? (dto / 100) * gross : dto) : 0;
    return sum + Math.max(0, gross - discountAmount);
  }, 0);

  // ──  Item-level discount total (for display) ─────────────────────────────
  const itemLevelDiscount: number = items.reduce((sum: number, item: any) => {
    const gross = (item?.unitPrice && item?.unitPrice * item?.quantity) || 0;
    const dto = item?.dto ?? 0;
    const itemDtoType: string = item?.dtoType ?? "amount";
    if (dto <= 0) return sum;
    const discountAmount =
      itemDtoType === "percentage" ? (dto / 100) * gross : dto;
    return sum + discountAmount;
  }, 0);

  // ── Form-level discount ─────────────────────────────────────────────────
  // Applies to the post-item-discount subtotal when percentage
  const formLevelDiscountAmount =
    discountType === "percentage"
      ? (formLevelDiscountRaw / 100) * subtotal
      : formLevelDiscountRaw;

  // Match Verifactu: item-level discounts take precedence; otherwise use the
  // form-level "additional discount" input. Do not double-count.
  const totalDiscount =
    itemLevelDiscount > 0 ? itemLevelDiscount : formLevelDiscountAmount;
  const discountToSubtract =
    itemLevelDiscount > 0 ? 0 : formLevelDiscountAmount;

  // ── Item-level VAT ──────────────────────────────────────────────────────
  let totalTax = items.reduce((sum: number, item: any) => {
    const gross = (item?.unitPrice && item?.unitPrice * item?.quantity) || 0;
    const dto = item?.dto ?? 0;
    const itemDtoType: string = item?.dtoType ?? "amount";
    const itemDiscountAmount =
      dto > 0 ? (itemDtoType === "percentage" ? (dto / 100) * gross : dto) : 0;
    const baseAfterItemDiscount = Math.max(0, gross - itemDiscountAmount);
    const tax = (baseAfterItemDiscount * item?.vatPercentage) / 100 || 0;
    return sum + tax;
  }, 0);

  // ── Additional (form-level) taxes ───────────────────────────────────────
  totalTax +=
    taxesArray.reduce((sum: number, tax: any) => {
      let calculatedTax = 0;
      const baseAmt = Number(tax?.baseAmount) || 0;
      if (tax.isWithHoldingTax !== undefined && !tax.isWithHoldingTax) {
        calculatedTax = (baseAmt * tax?.vatPercentage) / 100 || 0;
      } else if (tax?.taxType === "VAT" || tax?.taxType === "IGIC") {
        calculatedTax = (baseAmt * tax?.vatPercentage) / 100 || 0;
      }

      return sum + calculatedTax;
    }, 0) ?? 0;

  // ──  Withholding tax ─────────────────────────────────────────────────────
  const sumOfWithHoldingTax =
    taxesArray.reduce((sum: number, tax: any) => {
      let calculatedTax = 0;
      const baseAmt = Number(tax?.baseAmount) || 0;

      if (tax.isWithHoldingTax !== undefined && tax.isWithHoldingTax) {
        calculatedTax = (baseAmt * tax?.vatPercentage) / 100 || 0;
      } else if (tax?.taxType === "WHT" || tax?.taxType === "IRPF") {
        calculatedTax = (baseAmt * tax?.vatPercentage) / 100 || 0;
      }
      return sum + calculatedTax;
    }, 0) ?? 0;

  // ── Total  ───────────────────────────────────────────────────
  const total = subtotal + totalTax - sumOfWithHoldingTax - discountToSubtract;

  return { totalTax, totalDiscount, sumOfWithHoldingTax, total, subtotal };
};
